import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import CustomAlertCommodityConfig from './steps/CustomAlertCommodityConfig';
import CustomAlertTypeConfiguration from './steps/CustomAlertTypeConfiguration';
import NestedCommoditySelector from '../components/NestedCommoditySelector';
import { resolveCommodityName } from '../../utils/alertCommodity';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../constants/Routes';
import { isTierLimitError } from '../../utils/alertCreateError';
import { formatTierName } from '../../utils/formatTierName';
import { useUser } from '../../context/UserContext';
import { useCommodities } from '../../utils/api-hooks/commodity';
import { useCreateCustomAlert, useCreateSmartAlert, useRegions } from '../../utils/api-hooks/alerts';

const TYPE_META = {
  fob_price_alert: 'FOB price alert',
  fob_price: 'FOB price alert',
  price_change: 'FOB price alert',
  price_changes: 'FOB price alert',
  price: 'Price alert',
  volume: 'Volume change alert',
  weather: 'Weather impact alert',
  rain: 'Rain alert',
  temperature: 'Temperature alert',
  market: 'Market impact alert',
};

const SMART_STEP_LABELS = ['COMMODITY', 'ALERT TYPE', 'REGION'];
const CUSTOM_STEP_LABELS = ['COMMODITY', 'ALERT TYPE', 'CONDITION', 'REGION'];

const mapOperatorToApiFormat = (operator) => {
  const operatorMap = {
    greaterThan: 'greater_than',
    lessThan: 'less_than',
    equalTo: 'equals_to',
    inBetween: 'in_between',
  };
  return operatorMap[operator] || operator;
};

const isWeatherRelatedAlertType = (type) => ['rain', 'temperature'].includes(type);

const CUSTOM_ALERT_OPTIONS = [
  {
    id: 'price',
    title: 'Price alert',
    description: 'Set custom price thresholds for your commodity',
    iconSrc: '/assets/icons/alerts/price.png',
    iconWrapClass: 'bg-[#E8F8EC]',
  },
  {
    id: 'volume',
    title: 'Volume change alert',
    description: 'Define volume change conditions for your commodity',
    iconSrc: '/assets/icons/alerts/volume.png',
    iconWrapClass: 'bg-[#FFF7E8]',
  },
  {
    id: 'rain',
    title: 'Rain alert',
    description: 'Get notified when rainfall meets your criteria',
    iconSrc: '/assets/icons/alerts/weather.png',
    iconWrapClass: 'bg-[#EEF4FF]',
  },
  {
    id: 'temperature',
    title: 'Temperature alert',
    description: 'Get notified when temperature meets your criteria',
    iconSrc: '/assets/icons/alerts/weather.png',
    iconWrapClass: 'bg-[#EEF4FF]',
  },
];

const DEFAULT_SKU_SELECTION = {
  district: '',
  type: '',
  variety: '',
  size: '',
  grade: '',
  package: '',
  storage: '',
  condition: '',
  environment: '',
  quality: '',
  appearance: '',
  properties: '',
  types: [],
  varieties: [],
  origins: [],
};

const DEFAULT_CUSTOM_CONFIGURATION = {
  operator: null,
  granularity: null,
  value: '',
  minValue: '',
  maxValue: '',
};

const SMART_ALERT_OPTIONS = [
  {
    id: 'fob_price_alert',
    title: 'FOB price alert',
    description: 'Get notified when FOB prices change',
    iconSrc: '/assets/icons/alerts/price.png',
    iconWrapClass: 'bg-[#E8F8EC]',
  },
  {
    id: 'volume',
    title: 'Volume change alert',
    description: 'Get notified of volume changes',
    iconSrc: '/assets/icons/alerts/volume.png',
    iconWrapClass: 'bg-[#FFF7E8]',
  },
  {
    id: 'weather',
    title: 'Weather impact alert',
    description: 'Get notified about weather events',
    iconSrc: '/assets/icons/alerts/weather.png',
    iconWrapClass: 'bg-[#EEF4FF]',
  },
  {
    id: 'market',
    title: 'Market impact alert',
    description: 'Get notified about events that impact the market',
    iconSrc: '/assets/icons/alerts/market.png',
    iconWrapClass: 'bg-[#F5EDFF]',
  },
];

const smartTypeToApiAlertType = (specificAlertType) => {
  switch (specificAlertType) {
    case 'fob_price_alert':
    case 'fob_price':
    case 'price_change':
    case 'price_changes':
      return 'price';
    case 'volume':
      return 'volume';
    case 'weather':
      return 'weather';
    case 'market':
      return 'market';
    default:
      return 'price';
  }
};

const SmartAlertIcon = ({ size = 28 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 2.5l2.62 6.34 6.88.58-5.2 4.54 1.56 6.72L12 16.9l-5.86 3.78 1.56-6.72-5.2-4.54 6.88-.58L12 2.5z"
      stroke="var(--bloom-green)"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const CustomAlertIcon = ({ size = 28 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M6 4v16" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 4v16" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
    <path d="M18 4v16" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
    <rect x="4" y="14" width="4" height="3" rx="1.5" fill="#3B82F6" />
    <rect x="10" y="7" width="4" height="3" rx="1.5" fill="#3B82F6" />
    <rect x="16" y="11" width="4" height="3" rx="1.5" fill="#3B82F6" />
  </svg>
);

const AlertIcon = ({ src, alt = '', size = 36 }) => (
  <img
    src={src}
    alt={alt}
    width={size}
    height={size}
    className="object-contain"
    aria-hidden={alt ? undefined : true}
  />
);

const StepPreview = ({ stepCount, path }) => (
  <p className="mt-4 font-['Inter'] text-[14px] text-[#828382]">
    {stepCount} steps · {path}
  </p>
);

const SelectionCard = ({ active, icon, title, description, stepCount, path, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full rounded-[16px] border px-4 py-4 text-left transition-colors ${
      active
        ? 'border-bloom-green bg-[#F3FFF7]'
        : 'border-[#E3E5E4] bg-white hover:border-bloom-green/50'
    }`}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center">
            {icon}
          </span>
          <p className="font-['Inter'] text-[16px] font-semibold text-[#282A29]">{title}</p>
        </div>
        <p className="mt-2 font-['Inter'] text-[14px] leading-6 text-[#6F7371]">{description}</p>
        <StepPreview stepCount={stepCount} path={path} />
      </div>
      <span
        className={`mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
          active ? 'border-bloom-green bg-bloom-green text-white' : 'border-[#B9BEBC] bg-white'
        }`}
      >
        {active ? <Check size={14} /> : null}
      </span>
    </div>
  </button>
);

const SummaryRow = ({ label, value }) => (
  <div className="grid grid-cols-[110px_1fr] gap-4">
    <span className="font-['Inter'] text-[14px] text-[#6F7371]">{label}</span>
    <span className="font-['Inter'] text-[14px] font-medium text-[#282A29]">{value}</span>
  </div>
);

const ModalButton = ({ children, onClick, variant = 'primary', disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`rounded-[10px] px-7 py-3 font-['Inter'] text-[16px] font-medium transition-colors ${
      variant === 'primary'
        ? disabled
          ? 'cursor-not-allowed bg-[#8fd9ad] text-white'
          : 'bg-bloom-green text-white hover:bg-bloom-green-hover'
        : disabled
          ? 'cursor-not-allowed border border-[#A7DDBD] text-[#A7DDBD]'
          : 'border border-bloom-green text-bloom-green hover:bg-[#F3FFF7]'
    }`}
  >
    {children}
  </button>
);

const Stepper = ({ step, labels = SMART_STEP_LABELS }) => {
  const lineWidth = 'w-5 sm:w-14';

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center gap-1 sm:gap-3">
        {labels.map((label, index) => {
          const num = index + 1;
          const active = step === num;
          const done = step > num;
          const lineColor = step > num ? 'bg-bloom-green' : 'bg-[#E3E5E4]';

          return (
            <React.Fragment key={label}>
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-['Inter'] text-[14px] font-medium ${
                  active
                    ? 'border-bloom-green bg-bloom-green text-white'
                    : done
                      ? 'border-bloom-green text-bloom-green'
                      : 'border-[#CDD2D0] text-[#828382]'
                }`}
              >
                {num}
              </div>
              {index < labels.length - 1 && (
                <div className={`flex h-8 ${lineWidth} shrink-0 items-center`}>
                  <div className={`h-px w-full ${lineColor}`} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="mt-2 flex items-start gap-1 sm:gap-3">
        {labels.map((label, index) => {
          const num = index + 1;
          const active = step === num;

          return (
            <React.Fragment key={`${label}-label`}>
              <div className="flex w-8 shrink-0 justify-center overflow-visible">
                <span
                  className={`font-['Inter'] text-[11px] font-medium tracking-[0.08em] whitespace-nowrap max-sm:whitespace-normal max-sm:text-center max-sm:text-[9px] ${
                    active ? 'text-bloom-green' : 'text-[#828382]'
                  }`}
                >
                  {label}
                </span>
              </div>
              {index < labels.length - 1 && <div className={`${lineWidth} shrink-0`} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// Same grouped/searchable selector as Analytics. It emits lowercase names, so
// resolve back to the API-cased name; a miss leaves the selection untouched.
const CommodityDropdown = ({ commodities, selectedCommodity, onSelect }) => (
  <NestedCommoditySelector
    variant="stacked"
    portalMenu
    value={selectedCommodity}
    placeholder="Select commodity"
    onChange={(next) => {
      const name = resolveCommodityName(commodities, next);
      const current = String(selectedCommodity || '').trim().toLowerCase();
      if (name && name.toLowerCase() !== current) onSelect(name);
    }}
  />
);

const SmartTypeCard = ({ option, selected, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-[16px] border px-4 py-3 text-left transition-colors ${
        selected ? 'border-bloom-green bg-[#F3FFF7]' : 'border-[#E3E5E4] hover:border-bloom-green/50'
      }`}
    >
      <div className="flex items-center gap-4">
        <span className={`flex h-10 w-10 items-center justify-center rounded-[10px] ${option.iconWrapClass}`}>
          <AlertIcon src={option.iconSrc} size={36} />
        </span>
        <div>
          <p className="font-['Inter'] text-[16px] font-semibold text-[#282A29]">{option.title}</p>
          <p className="mt-1 font-['Inter'] text-[14px] text-[#6F7371]">{option.description}</p>
        </div>
      </div>
      <span
        className={`flex h-6 w-6 items-center justify-center rounded-[6px] border ${
          selected ? 'border-bloom-green bg-bloom-green' : 'border-[#CDD2D0] bg-white'
        }`}
      >
        {selected ? <Check size={14} className="text-white" /> : null}
      </span>
    </button>
  );
};

const RegionCheckbox = ({ checked, label, onToggle }) => (
  <button type="button" onClick={onToggle} className="flex items-center gap-2 text-left">
    <span
      className={`flex h-5 w-5 items-center justify-center rounded-[6px] border transition-colors duration-200 ease-out ${
        checked ? 'border-bloom-green bg-bloom-green' : 'border-[#81F2A9] bg-white'
      }`}
    >
      {checked ? <Check size={13} className="text-white" /> : null}
    </span>
    <span className="font-['Inter'] text-[14px] text-[#282A29]">{label}</span>
  </button>
);

const CountryCard = ({
  country,
  regions,
  selectedRegions,
  expanded,
  onToggleExpand,
  onToggleRegion,
  onSelectAll,
  onClearAll,
}) => (
  <div className="overflow-hidden rounded-[14px] border border-[#E3E5E4] bg-white">
    <button
      type="button"
      onClick={onToggleExpand}
      className="flex w-full items-center justify-between px-4 py-3 text-left"
    >
      <span className="font-['Inter'] text-[16px] font-semibold text-[#282A29]">{country}</span>
      <ChevronDown
        size={16}
        className={`text-[#828382] transition-transform duration-300 ease-out ${
          expanded ? 'rotate-180' : ''
        }`}
      />
    </button>
    <div
      className={`grid transition-[grid-template-rows] duration-300 ease-out ${
        expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      }`}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          className={`border-t border-[#E8EBE9] px-4 pb-4 pt-3 transition-opacity duration-300 ease-out ${
            expanded ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="flex items-center gap-5 pb-3">
            <button type="button" onClick={onSelectAll} className="font-['Inter'] text-[14px] text-bloom-green">
              Select all
            </button>
            <button type="button" onClick={onClearAll} className="font-['Inter'] text-[14px] text-[#EC3460]">
              Clear all
            </button>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3">
            {regions.map((region) => (
              <RegionCheckbox
                key={region}
                checked={selectedRegions.includes(region)}
                label={region}
                onToggle={() => onToggleRegion(country, region)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ModalShell = ({ children, onClose }) => (
  <div
    className="fixed inset-0 z-[80] flex items-center justify-center p-2 sm:p-4 backdrop-blur-[35px]"
    style={{ backgroundColor: '#D6D6D633' }}
    onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose?.();
    }}
  >
    <div
      className="relative max-h-[calc(100dvh-16px)] w-full max-w-[640px] overflow-auto rounded-[16px] sm:rounded-[24px] bg-white shadow-2xl"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-5 top-5 z-10 rounded-full p-1 text-[#828382] hover:bg-[#F0F5F2] hover:text-[#282A29] transition-colors"
        aria-label="Close"
      >
        <X size={18} />
      </button>
      {children}
    </div>
  </div>
);

const AlertCreateModal = ({ onClose }) => {
  const { email, tier } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { commodities } = useCommodities();
  const createSmartAlert = useCreateSmartAlert();
  const createCustomAlert = useCreateCustomAlert();
  const [phase, setPhase] = useState('choice');
  const [limitInfo, setLimitInfo] = useState(null);
  const [selectedMode, setSelectedMode] = useState('smart');
  const [successData, setSuccessData] = useState(null);
  const [smartStep, setSmartStep] = useState(1);
  const [selectedCommodity, setSelectedCommodity] = useState('');
  const [selectedSpecificAlertType, setSelectedSpecificAlertType] = useState('fob_price_alert');
  const [selectedRegions, setSelectedRegions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCountries, setExpandedCountries] = useState({});
  const [customStep, setCustomStep] = useState(1);
  const [customCommodity, setCustomCommodity] = useState('');
  const [customAlertType, setCustomAlertType] = useState('price');
  const [customRegions, setCustomRegions] = useState({});
  const [customSearchQuery, setCustomSearchQuery] = useState('');
  const [customExpandedCountries, setCustomExpandedCountries] = useState({});
  const [customConfiguration, setCustomConfiguration] = useState(DEFAULT_CUSTOM_CONFIGURATION);
  const [skuSelection, setSkuSelection] = useState(DEFAULT_SKU_SELECTION);

  const flatCommodities = useMemo(() => {
    const all = [
      ...(commodities?.Fruits || []),
      ...(commodities?.Vegetables || []),
      ...Object.values(commodities || {}).flatMap((v) => (Array.isArray(v) ? v : [])),
    ];
    const seen = new Set();
    return all.filter((item) => {
      const key = String(item).toLowerCase();
      if (!item || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort((a, b) => a.localeCompare(b));
  }, [commodities]);

  const derivedAlertType = smartTypeToApiAlertType(selectedSpecificAlertType);
  const isMarket = derivedAlertType === 'market';
  const smartStepLabels = isMarket ? SMART_STEP_LABELS.slice(0, 2) : SMART_STEP_LABELS;
  const smartRegionsEnabled =
    phase === 'wizard' && selectedMode === 'smart' && smartStep === 3 && Boolean(selectedCommodity);
  const customRegionsEnabled =
    phase === 'wizard' && selectedMode === 'custom' && customStep === 4 && Boolean(customCommodity);

  const {
    regions,
    isLoading: isLoadingRegions,
  } = useRegions(
    smartRegionsEnabled ? derivedAlertType : null,
    smartRegionsEnabled ? selectedCommodity : null,
    'default'
  );

  const {
    regions: customRegionsData,
    isLoading: isLoadingCustomRegions,
  } = useRegions(
    customRegionsEnabled ? customAlertType : null,
    customRegionsEnabled ? customCommodity : null,
    'custom'
  );

  const isCountryExpanded = (country, expandedMap) => {
    if (Object.prototype.hasOwnProperty.call(expandedMap, country)) {
      return expandedMap[country];
    }
    const lower = String(country || '').toLowerCase();
    return lower === 'usa' || lower === 'united states';
  };

  const toggleCountryExpanded = (country, setExpandedMap) => {
    setExpandedMap((prev) => ({
      ...prev,
      [country]: !isCountryExpanded(country, prev),
    }));
  };

  const successTypeLabel = useMemo(
    () => TYPE_META[successData?.specificAlertType] || TYPE_META[successData?.alertType] || '-',
    [successData]
  );

  const regionSummary = useMemo(() => {
    if (!successData?.regions || Object.keys(successData.regions).length === 0) return '-';
    const values = [];
    Object.entries(successData.regions).forEach(([parent, children]) => {
      if (Array.isArray(children) && children.length > 0) {
        children.forEach((child) => values.push(`${child}, ${parent}`));
      }
    });
    if (values.length === 0) return '-';
    if (values.length === 1) return values[0];
    return `${values[0]}, +${values.length - 1} others`;
  }, [successData]);

  const regionEntries = useMemo(() => {
    const entries = Object.entries(regions || {}).sort(([a], [b]) => {
      const aUSA = a.toLowerCase() === 'usa' || a.toLowerCase() === 'united states';
      const bUSA = b.toLowerCase() === 'usa' || b.toLowerCase() === 'united states';
      if (aUSA && !bUSA) return -1;
      if (!aUSA && bUSA) return 1;
      return a.localeCompare(b);
    });
    const query = searchQuery.trim().toLowerCase();
    if (!query) return entries;
    return entries.filter(([country, list]) => {
      if (country.toLowerCase().includes(query)) return true;
      return (list || []).some((item) => String(item).toLowerCase().includes(query));
    });
  }, [regions, searchQuery]);

  const customRegionEntries = useMemo(() => {
    const entries = Object.entries(customRegionsData || {}).sort(([a], [b]) => {
      const aUSA = a.toLowerCase() === 'usa' || a.toLowerCase() === 'united states';
      const bUSA = b.toLowerCase() === 'usa' || b.toLowerCase() === 'united states';
      if (aUSA && !bUSA) return -1;
      if (!aUSA && bUSA) return 1;
      return a.localeCompare(b);
    });
    const query = customSearchQuery.trim().toLowerCase();
    if (!query) return entries;
    return entries.filter(([country, list]) => {
      if (country.toLowerCase().includes(query)) return true;
      return (list || []).some((item) => String(item).toLowerCase().includes(query));
    });
  }, [customRegionsData, customSearchQuery]);

  const canCreateSmartAlert =
    isMarket || Object.values(selectedRegions).some((items) => items.length > 0);

  const canProceedCustomStep2 = useMemo(() => {
    if (!customAlertType) return false;
    if (isWeatherRelatedAlertType(customAlertType)) return true;
    if (customAlertType === 'price') {
      return Boolean(
        skuSelection.type &&
        skuSelection.variety &&
        skuSelection.size &&
        skuSelection.grade &&
        skuSelection.package
      );
    }
    if (customAlertType === 'volume') {
      return (
        Array.isArray(skuSelection.types) && skuSelection.types.length > 0 &&
        Array.isArray(skuSelection.varieties) && skuSelection.varieties.length > 0
      );
    }
    return true;
  }, [customAlertType, skuSelection]);

  const canProceedCustomStep3 = useMemo(() => {
    if (!customConfiguration.operator || !customConfiguration.granularity) return false;
    if (customConfiguration.operator === 'inBetween') {
      if (!customConfiguration.minValue || !customConfiguration.maxValue) return false;
      const min = parseFloat(customConfiguration.minValue);
      const max = parseFloat(customConfiguration.maxValue);
      return !Number.isNaN(min) && !Number.isNaN(max) && min < max;
    }
    return customConfiguration.value !== '';
  }, [customConfiguration]);

  const canCreateCustomAlert = Object.values(customRegions).some((items) => items.length > 0);

  const handleToggleRegion = (country, region) => {
    setSelectedRegions((prev) => {
      const current = prev[country] || [];
      const next = current.includes(region)
        ? current.filter((item) => item !== region)
        : [...current, region];
      return { ...prev, [country]: next };
    });
  };

  const handleToggleCustomRegion = (country, region) => {
    setCustomRegions((prev) => {
      const current = prev[country] || [];
      const next = current.includes(region)
        ? current.filter((item) => item !== region)
        : [...current, region];
      return { ...prev, [country]: next };
    });
  };

  const handleCustomAlertTypeSelect = (alertTypeId) => {
    setCustomAlertType(alertTypeId);
    setSkuSelection(DEFAULT_SKU_SELECTION);
  };

  const resetSmartFlow = () => {
    setSmartStep(1);
    setSelectedCommodity('');
    setSelectedSpecificAlertType('fob_price_alert');
    setSelectedRegions({});
    setSearchQuery('');
  };

  const resetCustomFlow = () => {
    setCustomStep(1);
    setCustomCommodity('');
    setCustomAlertType('price');
    setCustomRegions({});
    setCustomSearchQuery('');
    setCustomConfiguration(DEFAULT_CUSTOM_CONFIGURATION);
    setSkuSelection(DEFAULT_SKU_SELECTION);
  };

  const submitSmartAlert = async () => {
    const payload = {
      user_email: email,
      commodity: selectedCommodity,
      alert_type: derivedAlertType,
      specific_alert_type: selectedSpecificAlertType,
      regions: isMarket ? null : selectedRegions,
      enabled: true,
      user_tier: tier,
    };
    try {
      await createSmartAlert.mutateAsync(payload);
    } catch (error) {
      if (isTierLimitError(error)) {
        setLimitInfo({ type: 'smart', count: error.currentAlertCount, max: error.maxAllowedAlerts });
        setPhase('limit');
        return;
      }
      throw error;
    }
    setSuccessData({
      alertType: derivedAlertType,
      specificAlertType: selectedSpecificAlertType,
      commodity: selectedCommodity,
      regions: payload.regions,
    });
    setPhase('success');
  };

  const submitCustomAlert = async () => {
    const payload = {
      user_email: email,
      commodity: customCommodity,
      alert_type: customAlertType,
      operator: mapOperatorToApiFormat(customConfiguration.operator),
      regions: customRegions,
      enabled: true,
      granularity: [customConfiguration.granularity],
    };

    if (customConfiguration.operator === 'inBetween') {
      payload.min_value = customConfiguration.minValue;
      payload.max_value = customConfiguration.maxValue;
    } else {
      payload.value = customConfiguration.value;
    }

    if (customAlertType === 'price') {
      payload.skus = {
        district: skuSelection.district,
        variety: skuSelection.variety,
        size: skuSelection.size,
        grade: skuSelection.grade,
        type: skuSelection.type,
        sku: skuSelection.package,
        storage: skuSelection.storage,
        condition: skuSelection.condition,
        environment: skuSelection.environment,
        quality: skuSelection.quality,
        appearance: skuSelection.appearance,
        properties: skuSelection.properties,
      };
    }

    if (customAlertType === 'volume') {
      payload.skus = {
        types: skuSelection.types,
        varieties: skuSelection.varieties,
        origins: skuSelection.origins,
      };
    }

    try {
      await createCustomAlert.mutateAsync(payload);
    } catch (error) {
      if (isTierLimitError(error)) {
        setLimitInfo({ type: 'custom', count: error.currentAlertCount, max: error.maxAllowedAlerts });
        setPhase('limit');
        return;
      }
      throw error;
    }
    setSuccessData({
      alertType: 'custom',
      specificAlertType: customAlertType,
      commodity: customCommodity,
      regions: customRegions,
    });
    setPhase('success');
  };

  const limitPlan = formatTierName(tier) || t('alerts.limitReached.currentPlan');
  const limitType = limitInfo ? t(`alerts.limitReached.types.${limitInfo.type}`) : '';
  let limitMessage = t('alerts.limitReached.body');
  if (limitInfo && limitInfo.max === 0) {
    limitMessage = t('alerts.limitReached.notIncluded', { plan: limitPlan, type: limitType });
  } else if (limitInfo && limitInfo.max !== null && limitInfo.count !== null) {
    limitMessage = t('alerts.limitReached.bodyWithCount', {
      plan: limitPlan,
      type: limitType,
      // Legacy accounts can hold more than the plan allows (e.g. 29 vs 1): show "at the limit".
      count: Math.min(limitInfo.count, limitInfo.max),
      max: limitInfo.max,
    });
  }

  return (
    <ModalShell onClose={onClose}>
      {phase === 'choice' && (
        <div className="px-6 pb-8 pt-5 sm:px-8">
          <h2 className="text-center font-['Inter'] text-[20px] font-semibold text-[#282A29]">
            Create alert
          </h2>
          <p className="mt-8 font-['Inter'] text-[16px] text-[#282A29]">
            Choose how you want to set up your alert
          </p>
          <div className="mt-5 flex flex-col gap-5">
            <SelectionCard
              active={selectedMode === 'smart'}
              onClick={() => setSelectedMode('smart')}
              icon={<SmartAlertIcon size={28} />}
              title="Smart alert"
              description="Set up in 3 quick steps, the alert thresholds were set up by industry specialists."
              stepCount={3}
              path="Commodity → Alert type → Region"
            />
            <SelectionCard
              active={selectedMode === 'custom'}
              onClick={() => setSelectedMode('custom')}
              icon={<CustomAlertIcon size={28} />}
              title="Custom alert"
              description="Full control — define your own thresholds, conditions and frequency, tailored exactly to what you track."
              stepCount={4}
              path="Commodity → Alert type → Conditions → Region"
            />
          </div>
          <div className="mt-10 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] border border-bloom-green px-7 py-3 font-['Inter'] text-[16px] font-medium text-bloom-green hover:bg-[#F3FFF7] transition-colors"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setPhase('wizard')}
              className="rounded-[10px] bg-bloom-green px-7 py-3 font-['Inter'] text-[16px] font-medium text-white hover:bg-bloom-green-hover transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {phase === 'wizard' && selectedMode === 'custom' && (
        <div className="px-6 pb-8 pt-5 sm:px-8">
          <h2 className="text-center font-['Inter'] text-[20px] font-semibold text-[#282A29]">
            Create Custom alert
          </h2>
          <div className="mt-6">
            <Stepper step={customStep} labels={CUSTOM_STEP_LABELS} />
          </div>

          {customStep === 1 && (
            <>
              <div className="mt-10">
                <p className="font-['Inter'] text-[14px] text-[#282A29]">Select commodity</p>
                <div className="mt-4">
                  <CommodityDropdown
                    commodities={flatCommodities}
                    selectedCommodity={customCommodity}
                    onSelect={setCustomCommodity}
                  />
                </div>
                <p className="mt-4 font-['Inter'] text-[14px] text-[#828382]">
                  Choose the commodity you want this alert to track.
                </p>
              </div>
              <div className="mt-40 flex justify-end gap-3">
                <ModalButton variant="secondary" onClick={() => setPhase('choice')}>Back</ModalButton>
                <ModalButton onClick={() => setCustomStep(2)} disabled={!customCommodity}>Next</ModalButton>
              </div>
            </>
          )}

          {customStep === 2 && (
            <>
              <div className="mt-10">
                <p className="font-['Inter'] text-[16px] text-[#282A29]">
                  What type of changes do you want to be alerted about?
                </p>
                <div className="mt-6 flex flex-col gap-4">
                  {CUSTOM_ALERT_OPTIONS.map((option) => (
                    <SmartTypeCard
                      key={option.id}
                      option={option}
                      selected={customAlertType === option.id}
                      onClick={() => handleCustomAlertTypeSelect(option.id)}
                    />
                  ))}
                </div>
                {!isWeatherRelatedAlertType(customAlertType) && (
                  <div className="mt-8">
                    <p className="mb-4 font-['Inter'] text-[14px] text-[#282A29]">
                      Configure the product details for this alert
                    </p>
                    <CustomAlertCommodityConfig
                      selectedAlertType={customAlertType}
                      selectedCommodity={customCommodity}
                      onCommodityToggle={setCustomCommodity}
                      skuSelection={skuSelection}
                      onSkuChange={setSkuSelection}
                      hideCommoditySelector
                      variant="modal"
                    />
                  </div>
                )}
              </div>
              <div className="mt-10 flex justify-end gap-3">
                <ModalButton variant="secondary" onClick={() => setCustomStep(1)}>Back</ModalButton>
                <ModalButton onClick={() => setCustomStep(3)} disabled={!canProceedCustomStep2}>Next</ModalButton>
              </div>
            </>
          )}

          {customStep === 3 && (
            <>
              <div className="mt-10">
                <p className="font-['Inter'] text-[16px] text-[#282A29]">
                  Define your alert conditions
                </p>
                <div className="mt-6">
                  <CustomAlertTypeConfiguration
                    selectedAlertType={customAlertType}
                    configuration={customConfiguration}
                    onConfigurationChange={setCustomConfiguration}
                    variant="modal"
                  />
                </div>
              </div>
              <div className="mt-10 flex justify-end gap-3">
                <ModalButton variant="secondary" onClick={() => setCustomStep(2)}>Back</ModalButton>
                <ModalButton onClick={() => setCustomStep(4)} disabled={!canProceedCustomStep3}>Next</ModalButton>
              </div>
            </>
          )}

          {customStep === 4 && (
            <>
              <div className="mt-10">
                <p className="font-['Inter'] text-[16px] text-[#282A29]">Select regions to receive alerts for</p>
                <div className="relative mt-5">
                  <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A3A1]" />
                  <input
                    value={customSearchQuery}
                    onChange={(e) => setCustomSearchQuery(e.target.value)}
                    placeholder="Search the country"
                    className="h-[38px] w-full rounded-[12px] border border-[#D5D8D6] pl-10 pr-4 font-['Inter'] text-[15px] text-[#282A29] placeholder:text-[#A0A3A1] focus:border-bloom-green focus:outline-none"
                  />
                </div>
                <div className="mt-4 flex flex-col gap-4">
                  {isLoadingCustomRegions ? (
                    <div role="status" aria-busy="true" aria-label="Loading" className="flex flex-col gap-4">
                      <div className="skeleton-sheen h-14 w-full rounded-[14px]" />
                      <div className="skeleton-sheen h-14 w-full rounded-[14px]" />
                      <div className="skeleton-sheen h-14 w-full rounded-[14px]" />
                    </div>
                  ) : (
                    customRegionEntries.map(([country, list]) => (
                      <CountryCard
                        key={country}
                        country={country}
                        regions={(list || []).filter(Boolean)}
                        selectedRegions={customRegions[country] || []}
                        expanded={isCountryExpanded(country, customExpandedCountries)}
                        onToggleExpand={() => toggleCountryExpanded(country, setCustomExpandedCountries)}
                        onToggleRegion={handleToggleCustomRegion}
                        onSelectAll={() =>
                          setCustomRegions((prev) => ({ ...prev, [country]: [...(list || [])] }))
                        }
                        onClearAll={() =>
                          setCustomRegions((prev) => ({ ...prev, [country]: [] }))
                        }
                      />
                    ))
                  )}
                </div>
              </div>
              <div className="mt-10 flex justify-end gap-3">
                <ModalButton variant="secondary" onClick={() => setCustomStep(3)}>Back</ModalButton>
                <ModalButton
                  onClick={submitCustomAlert}
                  disabled={!canCreateCustomAlert || createCustomAlert.isPending}
                >
                  {createCustomAlert.isPending ? 'Creating...' : 'Create alert'}
                </ModalButton>
              </div>
            </>
          )}
        </div>
      )}

      {phase === 'wizard' && selectedMode === 'smart' && (
        <div className="px-6 pb-8 pt-5 sm:px-8">
          <h2 className="text-center font-['Inter'] text-[20px] font-semibold text-[#282A29]">
            Create Smart alert
          </h2>
          <div className="mt-6">
            <Stepper step={smartStep} labels={smartStepLabels} />
          </div>

          {smartStep === 1 && (
            <>
              <div className="mt-10">
                <p className="font-['Inter'] text-[14px] text-[#282A29]">Select commodity</p>
                <div className="mt-4">
                  <CommodityDropdown
                    commodities={flatCommodities}
                    selectedCommodity={selectedCommodity}
                    onSelect={setSelectedCommodity}
                  />
                </div>
                <p className="mt-4 font-['Inter'] text-[14px] text-[#828382]">
                  Choose the commodity you want this alert to track.
                </p>
              </div>
              <div className="mt-40 flex justify-end gap-3">
                <ModalButton variant="secondary" onClick={() => setPhase('choice')}>Back</ModalButton>
                <ModalButton onClick={() => setSmartStep(2)} disabled={!selectedCommodity}>Next</ModalButton>
              </div>
            </>
          )}

          {smartStep === 2 && (
            <>
              <div className="mt-10">
                <p className="font-['Inter'] text-[16px] text-[#282A29]">
                  What type of changes do you want to be alerted about?
                </p>
                <div className="mt-6 flex flex-col gap-4">
                  {SMART_ALERT_OPTIONS.map((option) => (
                    <SmartTypeCard
                      key={option.id}
                      option={option}
                      selected={selectedSpecificAlertType === option.id}
                      onClick={() => setSelectedSpecificAlertType(option.id)}
                    />
                  ))}
                </div>
              </div>
              <div className="mt-10 flex justify-end gap-3">
                <ModalButton variant="secondary" onClick={() => setSmartStep(1)}>Back</ModalButton>
                {isMarket ? (
                  <ModalButton onClick={submitSmartAlert} disabled={createSmartAlert.isPending}>
                    {createSmartAlert.isPending ? 'Creating...' : 'Create alert'}
                  </ModalButton>
                ) : (
                  <ModalButton onClick={() => setSmartStep(3)}>Next</ModalButton>
                )}
              </div>
            </>
          )}

          {smartStep === 3 && (
            <>
              <div className="mt-10">
                <p className="font-['Inter'] text-[16px] text-[#282A29]">Select regions to receive alerts for</p>
                <div className="relative mt-5">
                  <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A3A1]" />
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search the country"
                    className="h-[38px] w-full rounded-[12px] border border-[#D5D8D6] pl-10 pr-4 font-['Inter'] text-[15px] text-[#282A29] placeholder:text-[#A0A3A1] focus:border-bloom-green focus:outline-none"
                  />
                </div>
                <div className="mt-4 flex flex-col gap-4">
                  {isLoadingRegions ? (
                    <div role="status" aria-busy="true" aria-label="Loading" className="flex flex-col gap-4">
                      <div className="skeleton-sheen h-14 w-full rounded-[14px]" />
                      <div className="skeleton-sheen h-14 w-full rounded-[14px]" />
                      <div className="skeleton-sheen h-14 w-full rounded-[14px]" />
                    </div>
                  ) : (
                    regionEntries.map(([country, list]) => (
                      <CountryCard
                        key={country}
                        country={country}
                        regions={(list || []).filter(Boolean)}
                        selectedRegions={selectedRegions[country] || []}
                        expanded={isCountryExpanded(country, expandedCountries)}
                        onToggleExpand={() => toggleCountryExpanded(country, setExpandedCountries)}
                        onToggleRegion={handleToggleRegion}
                        onSelectAll={() =>
                          setSelectedRegions((prev) => ({ ...prev, [country]: [...(list || [])] }))
                        }
                        onClearAll={() =>
                          setSelectedRegions((prev) => ({ ...prev, [country]: [] }))
                        }
                      />
                    ))
                  )}
                </div>
              </div>
              <div className="mt-10 flex justify-end gap-3">
                <ModalButton variant="secondary" onClick={() => setSmartStep(2)}>Back</ModalButton>
                <ModalButton onClick={submitSmartAlert} disabled={!canCreateSmartAlert || createSmartAlert.isPending}>
                  {createSmartAlert.isPending ? 'Creating...' : 'Create alert'}
                </ModalButton>
              </div>
            </>
          )}
        </div>
      )}

      {phase === 'limit' && (
        <div role="alert" className="flex flex-col items-center px-6 pb-10 pt-12 text-center sm:px-10">
          <h2 className="font-['Inter'] text-[20px] font-semibold text-[#282A29]">
            {t('alerts.limitReached.title')}
          </h2>
          <p className="mt-4 max-w-[420px] font-['Inter'] text-[16px] leading-8 text-[#6F7371]">
            {limitMessage}
          </p>
          {/* Same Cancel / primary pair as the app's other confirm dialogs (Paywall). */}
          <div className="mt-8 flex w-full flex-wrap items-center justify-center gap-[12px]">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[8px] border border-[#E8EBE9] px-[24px] py-[10px] font-['Inter'] text-[14px] font-medium text-[#282A29] transition-colors hover:bg-[#F0F5F2]"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(ROUTES.PAYWALL);
              }}
              className="rounded-[8px] bg-bloom-green px-[24px] py-[10px] font-['Inter'] text-[14px] font-medium text-white transition-colors hover:bg-bloom-green-hover"
            >
              {t('alerts.limitReached.upgrade')}
            </button>
          </div>
        </div>
      )}

      {phase === 'success' && (
        <div className="px-6 pb-8 pt-6 sm:px-8">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#9BE8B4] bg-[#F3FFF7]">
            <Check size={40} className="text-bloom-green" />
          </div>
          <h2 className="mt-6 text-center font-['Inter'] text-[20px] font-semibold text-[#282A29]">
            {selectedMode === 'smart' ? 'Smart alert created' : 'Custom alert created'}
          </h2>
          <p className="mx-auto mt-4 max-w-[420px] text-center font-['Inter'] text-[16px] leading-8 text-[#6F7371]">
            Your alert for <span className="font-semibold text-[#282A29]">{successData?.commodity}</span> is now active.
          </p>
          <div className="mx-auto mt-8 max-w-[380px] rounded-[16px] bg-[#F3FFF7] px-5 py-5">
            <div className="flex flex-col gap-3">
              <SummaryRow label="Commodity" value={successData?.commodity || '-'} />
              <SummaryRow label="Alert type" value={successTypeLabel} />
              {successData?.alertType !== 'market' && <SummaryRow label="Region" value={regionSummary} />}
            </div>
          </div>
          <div className="mt-10 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] border border-bloom-green px-7 py-3 font-['Inter'] text-[16px] font-medium text-bloom-green hover:bg-[#F3FFF7] transition-colors"
            >
              Back to alerts
            </button>
            <button
              type="button"
              onClick={() => {
                setSuccessData(null);
                if (selectedMode === 'smart') resetSmartFlow();
                else resetCustomFlow();
                setPhase('choice');
              }}
              className="rounded-[10px] bg-bloom-green px-7 py-3 font-['Inter'] text-[16px] font-medium text-white hover:bg-bloom-green-hover transition-colors"
            >
              Create another
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
};

export default AlertCreateModal;
